import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import RawMaterialService from '../services/RawMaterialService';
import { isEmpty } from 'lodash';
import FinishedGoodService from '../services/FinishedGoodService';

const initialState = {
  data: [],
  datas: [],
  parent_datas: [],
  formError: [],
  pagination: {
    page: 0,
    per_page: 10,
    total: 10,
    totalPages: 10,
  },
  error: null,
  isSuccess: false,
  loading: false,
  submitLoading: false,
  isOpenFinishedGoodDialog: false,
  form: {
    initialValues: null,
    action: 'create',
    isOpen: false,
    isProduction: false,
  },
  notOrderableCount: 0,
};

export const fetchFinishedGoodList = createAsyncThunk(
  'finishedGood/fetchFinishedGoodList',
  async ({ perPage, page, keyword, type = '', status = '', is_orderable = '' }) => {
    try {
      const resp = await FinishedGoodService.List({ perPage, page, keyword, type, status, is_orderable });
      return resp;
    } catch (e) {
      return e.response;
    }
  },
);

export const createFinishedGood = createAsyncThunk(
  'finishedGood/createFinishedGood',
  async (data, { rejectWithValue }) => {
    try {
      const resp = await FinishedGoodService.Create(data);
      return resp;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

export const updateFinishedGood = createAsyncThunk(
  'finishedGood/updateFinishedGood',
  async (payload, { rejectWithValue }) => {
    try {
      const resp = await FinishedGoodService.Put(payload);
      return resp;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

export const deleteFinishedGood = createAsyncThunk('finishedGood/deleteFinishedGood', async (id) => {
  try {
    const resp = await FinishedGoodService.Delt(id);
    return resp;
  } catch (e) {
    return e.response;
  }
});

export const fetchActiveFinishedGoods = createAsyncThunk('finisheGood/all', async () => {
  try {
    const resp = await RawMaterialService.All();
    return resp;
  } catch (e) {
    return e.response;
  }
});

export const changePrice = createAsyncThunk(
  'finishedGood/changePrice',
  async (payload, { rejectWithValue }) => {
    try {
      const resp = await FinishedGoodService.ChangePrice(payload);
      return resp;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

export const finishedGoodActivate = createAsyncThunk(
  'finishedGood/activate',
  async (payload, { rejectWithValue }) => {
    try {
      const resp = await FinishedGoodService.Activate(payload);
      return resp;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

export const toggleFinishedGoodOrderable = createAsyncThunk(
  'finishedGood/toggleOrderable',
  async (payload, { rejectWithValue }) => {
    try {
      const resp = await FinishedGoodService.ToggleOrderable(payload);
      return resp;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

const finishedGoodSlice = createSlice({
  name: 'finishedGood',
  initialState: initialState,
  reducers: {
    openFinishedGoodDialog: (state, action) => {
      state.isOpenFinishedGoodDialog = action.payload;
    },
    setFormState: (state, action) => {
      state.form = {
        ...state.form,
        ...action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFinishedGoodList.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(fetchFinishedGoodList.fulfilled, (state, action) => {
        if (action.payload?.status === 200) {
          state.datas = [...action.payload?.data.data?.finishedGoodList];
          state.pagination = action.payload?.data.data?.pagination;
          state.notOrderableCount = action.payload?.data.data?.not_orderable_count ?? 0;
        } else {
          state.error = action.payload?.data.message;
          toast.error(state.error);
        }
        state.loading = false;
      })
      .addCase(fetchFinishedGoodList.rejected, (state, action) => {
        state.error = action.payload?.data;
        state.loading = false;
      })
      .addCase(createFinishedGood.pending, (state, action) => {
        state.submitLoading = true;
        state.formError = [];
        state.isSuccess = false;
      })
      .addCase(createFinishedGood.fulfilled, (state, action) => {
        state.datas.unshift(action.payload.data.data);
        toast.success(action.payload?.message);
        state.pagination = {
          ...state.pagination,
          total: state.pagination.total + 1,
        };
        state.submitLoading = false;
        state.isSuccess = true;
        state.form = {
          initialValues: null,
          action: 'create',
          isOpen: false,
          isProduction: false,
        };
      })
      .addCase(createFinishedGood.rejected, (state, action) => {
        const errors = action.payload?.data.errors;
        state.submitLoading = false;
        state.isSuccess = false;
        if (isEmpty(errors)) {
          toast.error(action.payload?.data.message || 'Something went wrong');
        } else {
          state.formError = errors;
        }
      })
      .addCase(updateFinishedGood.pending, (state, action) => {
        state.info = '';
        state.submitLoading = true;
      })
      .addCase(updateFinishedGood.fulfilled, (state, action) => {
        const index = state.datas.findIndex((client) => client.id === action.payload.data.data.id);
        state.datas[index] = {
          ...state[index],
          ...action.payload.data.data,
        };
        // toast.success(action.payload?.data.message);
        state.submitLoading = false;
        state.form = {
          initialValues: null,
          action: 'create',
          isOpen: false,
          isProduction: false,
        };
      })
      .addCase(updateFinishedGood.rejected, (state, action) => {
        state.submitLoading = false;
        const errors = action.payload?.data?.errors;
        state.isSuccess = false;
        if (isEmpty(errors)) {
          toast.error(action.payload?.data.message || 'Something went wrong');
        } else {
          state.formError = errors;
        }
      })
      .addCase(deleteFinishedGood.pending, (state, action) => {
        state.info = '';
        state.submitLoading = true;
      })
      .addCase(deleteFinishedGood.fulfilled, (state, action) => {
        const deletedId = action.meta.arg;
        state.datas = state.datas.filter((item) => item.id !== deletedId);
        toast.success(action.payload?.data.message);
        state.submitLoading = false;
        state.isOpenFinishedGoodDialog = false;
        state.pagination = {
          ...state.pagination,
          total: state.pagination.total > 0 ? state.pagination.total - 1 : 0,
        };
      })
      .addCase(deleteFinishedGood.rejected, (state, action) => {
        state.error = action.payload?.data;
        state.submitLoading = false;
      })
      .addCase(fetchActiveFinishedGoods.pending, (state, action) => {
        state.info = '';
        state.submitLoading = true;
      })
      .addCase(fetchActiveFinishedGoods.fulfilled, (state, action) => {
        if (action.payload?.status === 200) {
          state.datas = [...action.payload?.data?.data];
        } else {
          state.error = action.payload?.data.message;
          toast.error(state.error);
        }
        state.loading = false;
      })
      .addCase(changePrice.pending, (state, action) => {
        state.submitLoading = true;
        state.isSuccess = false;
      })
      .addCase(changePrice.fulfilled, (state, action) => {
        const updatedData = action.payload.data.data;
        const index = state.datas.findIndex((item) => item.sku === updatedData.sku);

        if (index !== -1) {
          state.datas[index].unit_price = updatedData.new_price;
        }

        toast.success(action.payload?.data.message);
        state.submitLoading = false;
        state.isSuccess = true;
      })
      .addCase(changePrice.rejected, (state, action) => {
        state.submitLoading = false;
        const errors = action.payload?.data?.errors;
        state.isSuccess = false;
        if (isEmpty(errors)) {
          toast.error(action.payload?.data.message || 'Something went wrong');
        } else {
          state.formError = errors;
        }
      }).addCase(finishedGoodActivate.pending, (state, action) => {
        state.info = '';
        state.submitLoading = true;
      })
      .addCase(finishedGoodActivate.fulfilled, (state, action) => {
        const index = state.datas.findIndex((client) => client.id === action.payload.data.data.id);
        state.datas[index] = {
          ...state[index],
          ...action.payload.data.data,
        };
        state.submitLoading = false;
        const isActive = action.payload.data.data.is_active;

        toast.success(
          isActive
            ? 'Finished good activated successfully.'
            : 'Finished good deactivated successfully.'
        );
      })
      .addCase(finishedGoodActivate.rejected, (state, action) => {
        state.submitLoading = false;
        const errors = action.payload?.data?.errors;
        state.isSuccess = false;
        if (isEmpty(errors)) {
          toast.error(action.payload?.data.message || 'Something went wrong');
        } else {
          state.formError = errors;
        }
      })
      .addCase(toggleFinishedGoodOrderable.pending, (state) => {
        state.submitLoading = true;
      })
      .addCase(toggleFinishedGoodOrderable.fulfilled, (state, action) => {
        const index = state.datas.findIndex((item) => item.id === action.payload.data.data.id);
        if (index !== -1) {
          state.datas[index] = {
            ...state.datas[index],
            ...action.payload.data.data,
          };
        }
        state.submitLoading = false;
        const isOrderable = action.payload.data.data.is_orderable;
        if (isOrderable) {
          state.notOrderableCount = Math.max(0, state.notOrderableCount - 1);
        } else {
          state.notOrderableCount += 1;
        }
        toast.success(
          isOrderable
            ? 'Finished good is now orderable.'
            : 'Finished good is now not orderable.'
        );
      })
      .addCase(toggleFinishedGoodOrderable.rejected, (state, action) => {
        state.submitLoading = false;
        const errors = action.payload?.data?.errors;
        state.isSuccess = false;
        if (isEmpty(errors)) {
          toast.error(action.payload?.data.message || 'Something went wrong');
        } else {
          state.formError = errors;
        }
      });
  },
});

const { reducer } = finishedGoodSlice;

export const { openFinishedGoodDialog, setFormState } = finishedGoodSlice.actions;

export const finishedGoodSelector = (state) => state?.finishedGood;

export default reducer;
